import React from 'react';
import { getIntl } from '../../../../../utils/intl';
import { useGoBack } from '../../../../../utils/appNavigationNames';
import useUser from '../../../../../hooks/api/useUser';
import { useSignOutAndResetApp } from '../../../../../hooks/useSignOutAndResetApp';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import InfoIcon from '../../../../v2BaseComponents/InfoIcon';
import Text from '../../../../v2BaseComponents/Text';
import { Input } from '../../../../v2BaseComponents/Inputs';
import useOrganization from '../../../../../hooks/api/useOrganization';
import MaxWidthRestrictedContent from '../../../../v2BaseComponents/MaxWidthRestrictedContent';

const orgOwnerIntl = getIntl('organization-owner-deletion-confirmation', {
    header: 'You Are About to Delete Your Organization',
    warningContent:
        "Because you are the owner of this organization, this will delete your account as well as all of your organization's data.  Are you sure you want to continue?",
    delete: 'Delete Organization',
    cancel: 'Cancel',
    confirmationText: 'Delete {organizationName}',
    toConfirm: 'Please input "{confirmationText}" to confirm',
});

const proUserIntl = getIntl('organization-pro-user-deletion-confirmation', {
    header: 'You Are About to Delete Your Account',
    warningContent: 'Your account will be permanently deleted and you will be removed from your organization',
    delete: 'Yes, Delete Account',
    cancel: 'Cancel',
});

const AccountOwnerDeleteConfirmation: React.FC<ProDeleteAccountConfirmationProps> = () => {
    const goBack = useGoBack();
    const [confirmationTextInput, setConfirmationTextInput] = React.useState('');
    const { signOutAndResetApp, isResetting } = useSignOutAndResetApp();
    const { deleteUser, isDeletingUser } = useUser({
        onDeleteSuccess: signOutAndResetApp,
    });
    const { myOrganization, isLoadingMyOrganization } = useOrganization({});
    const confirmationText = orgOwnerIntl('confirmationText', {
        organizationName: myOrganization?.organization?.metadata?.name || '',
    });
    const isDeleteEnabled = confirmationTextInput === confirmationText;

    return (
        <AnimateLoadedContent isLoading={isDeletingUser || isLoadingMyOrganization}>
            {() => (
                <ActionButtonLayout
                    whiteBackground
                    scrollable
                    actions={[
                        {
                            icon: 'X',
                            title: orgOwnerIntl('cancel'),
                            isDisabled: isDeletingUser,
                            onPress: goBack,
                            variant: 'secondary',
                            testID: 'cancel-delete-account-button',
                        },
                        {
                            icon: 'Trash2',
                            title: orgOwnerIntl('delete'),
                            isDisabled: !isDeleteEnabled || isResetting,
                            onPress: () => {
                                deleteUser();
                            },
                            variant: 'alert',
                            testID: 'delete-account-button',
                        },
                    ]}
                >
                    <MainContentContainer>
                        <MaxWidthRestrictedContent maxWidth={500}>
                            <ContentStack>
                                <InfoIcon
                                    alertIconProps={{
                                        name: 'AlertTriangle',
                                        variant: 'error',
                                    }}
                                    title={orgOwnerIntl('header')}
                                />
                                <Text variant="body-1">{orgOwnerIntl('warningContent')}</Text>
                                <Stack vertical fullWidth align="center" style={{ marginTop: 10 }}>
                                    <Input
                                        value={confirmationTextInput}
                                        onChange={(e) => setConfirmationTextInput(e.nativeEvent.text)}
                                        title={orgOwnerIntl('toConfirm', {
                                            confirmationText,
                                        })}
                                        isDisabled={isDeletingUser || isResetting}
                                        forceFullWidth
                                        testID="org-owner-account-deletion-confirmation-input"
                                    />
                                </Stack>
                            </ContentStack>
                        </MaxWidthRestrictedContent>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

const ProUserDeleteConfirmation: React.FC<ProDeleteAccountConfirmationProps> = () => {
    const goBack = useGoBack();
    const { signOutAndResetApp, isResetting } = useSignOutAndResetApp();
    const { deleteUser, isDeletingUser } = useUser({
        onDeleteSuccess: signOutAndResetApp,
    });

    return (
        <AnimateLoadedContent isLoading={isDeletingUser}>
            {() => (
                <ActionButtonLayout
                    whiteBackground
                    scrollable
                    actions={[
                        {
                            icon: 'X',
                            title: proUserIntl('cancel'),
                            isDisabled: isDeletingUser,
                            onPress: goBack,
                            variant: 'secondary',
                            testID: 'cancel-delete-account-button',
                        },
                        {
                            icon: 'Trash2',
                            title: proUserIntl('delete'),
                            isDisabled: isResetting,
                            onPress: () => {
                                deleteUser();
                            },
                            variant: 'alert',
                            testID: 'delete-account-button',
                        },
                    ]}
                >
                    <MainContentContainer>
                        <MaxWidthRestrictedContent maxWidth={500}>
                            <ContentStack>
                                <InfoIcon
                                    alertIconProps={{
                                        name: 'AlertTriangle',
                                        variant: 'error',
                                    }}
                                    title={proUserIntl('header')}
                                />
                                <Text variant="body-1">{proUserIntl('warningContent')}</Text>
                            </ContentStack>
                        </MaxWidthRestrictedContent>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export type ProDeleteAccountConfirmationOwnProps = {};

export type ProDeleteAccountConfirmationProps = ProDeleteAccountConfirmationOwnProps;

const ProDeleteAccountConfirmation: React.FC<ProDeleteAccountConfirmationProps> = () => {
    const { myOrganization, isLoadingMyOrganization } = useOrganization({});

    return (
        <AnimateLoadedContent isLoading={isLoadingMyOrganization}>
            {() =>
                myOrganization?.role === 'owner' ? <AccountOwnerDeleteConfirmation /> : <ProUserDeleteConfirmation />
            }
        </AnimateLoadedContent>
    );
};

export default React.memo(ProDeleteAccountConfirmation);
